import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, CheckCircle2, ChevronRight, Loader2, UserX } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { relativeFr } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORY_LABELS = {
  athletes: "Athlètes",
  prospecting: "Prospection",
  contracts: "Contrats",
  documents: "Documents",
  followups: "Relances",
  career: "Carrière",
  discussions: "Discussions",
  calendar: "Calendrier",
  team: "Équipe",
};
const DATE_RANGES = [
  { v: "7", label: "7 derniers jours" },
  { v: "30", label: "30 derniers jours" },
  { v: "90", label: "90 derniers jours" },
];

function isoDaysAgo(days) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

function ActorAvatar({ actor, tf, size = "h-8 w-8" }) {
  if (!actor) {
    return <div className={`${size} rounded-lg bg-secondary border border-border flex items-center justify-center text-[10px] text-muted-foreground shrink-0`}>?</div>;
  }
  if (actor.type === "system") {
    return <div className={`${size} rounded-lg bg-secondary border border-border flex items-center justify-center shrink-0`}><Activity className="h-3.5 w-3.5 text-muted-foreground" /></div>;
  }
  if (actor.picture) {
    return <img src={actor.picture} alt={actor.name} className={`${size} rounded-lg object-cover border border-border shrink-0`} />;
  }
  return (
    <div className={`${size} rounded-lg bg-secondary border border-border flex items-center justify-center text-xs font-bold shrink-0`}>
      {(actor.name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

export function actorLabel(actor, tf) {
  if (!actor) return tf("Membre inconnu");
  if (actor.type === "system") return tf("Système Athlynx");
  return actor.name || tf("Membre inconnu");
}

export const TeamActivity = () => {
  const { tf } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [member, setMember] = useState("all");
  const [category, setCategory] = useState("all");
  const [range, setRange] = useState("7");
  const [loadingMore, setLoadingMore] = useState(false);
  const [denied, setDenied] = useState(false);

  const params = useCallback((cursor) => ({
    limit: 15,
    cursor: cursor || undefined,
    actor_id: member !== "all" ? member : undefined,
    category: category !== "all" ? category : undefined,
    date_from: isoDaysAgo(parseInt(range, 10)),
  }), [member, category, range]);

  const load = useCallback(async () => {
    try {
      const [act, ind] = await Promise.all([
        api.get("/team/activity", { params: params() }),
        api.get("/team/indicators", { params: { days: parseInt(range, 10) } }),
      ]);
      setData(act.data);
      setIndicators(ind.data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    }
  }, [params, range]);
  useEffect(() => { load(); }, [load]);

  if (denied) return null;
  if (!data) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get("/team/activity", { params: params(data.next_cursor) });
      setData((d) => ({ ...r.data, items: [...d.items, ...r.data.items] }));
    } finally { setLoadingMore(false); }
  };

  const totals = indicators?.totals || {};

  return (
    <section className="rounded-2xl border border-border bg-surface/60 p-5 space-y-4" data-testid="team-activity">
      <div className="flex items-center gap-2.5">
        <Activity className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold flex-1">{tf("Activité de l'équipe")}</h2>
      </div>

      {/* Deterministic coordination indicators — real DB counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="team-indicators">
        {[
          { icon: CheckCircle2, label: tf("Relances terminées"), value: (indicators?.members || []).reduce((s, m) => s + m.followups_completed, 0), tone: "text-success" },
          { icon: AlertTriangle, label: tf("Relances en retard"), value: totals.overdue_followups ?? 0, tone: (totals.overdue_followups || 0) > 0 ? "text-danger" : "text-muted-foreground" },
          { icon: Activity, label: tf("Relances ouvertes"), value: totals.open_followups ?? 0, tone: "text-muted-foreground" },
          { icon: UserX, label: tf("Athlètes sans activité récente"), value: totals.inactive_players ?? 0, tone: "text-warning" },
        ].map((c, i) => (
          <div key={i} className="rounded-xl border border-border bg-secondary/30 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground leading-tight">
              <c.icon className={`h-3 w-3 shrink-0 ${c.tone}`} />
              <span className="truncate">{c.label}</span>
            </div>
            <div className="text-lg font-semibold mt-0.5">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Per-member recorded actions (counts, not performance scores) */}
      <div className="flex flex-wrap gap-2" data-testid="team-member-counts">
        {(indicators?.members || []).map((m) => (
          <button key={m.user_id} onClick={() => setMember(member === m.user_id ? "all" : m.user_id)}
            className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs transition-colors ${member === m.user_id ? "border-athlynx/40 bg-athlynx/10" : "border-border bg-secondary/30 hover:bg-secondary/60"}`}
            data-testid={`team-member-chip-${m.user_id}`}>
            <ActorAvatar actor={m} tf={tf} size="h-5 w-5" />
            <span className="font-medium">{m.name}</span>
            <span className="text-muted-foreground">{m.actions} {tf("actions")}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={member} onValueChange={setMember}>
          <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="team-filter-member"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Tous les membres")}</SelectItem>
            {(data.members || []).map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
            <SelectItem value="system">{tf("Système Athlynx")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="team-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Toutes les catégories")}</SelectItem>
            {(data.categories || []).map((c) => <SelectItem key={c} value={c}>{tf(CATEGORY_LABELS[c] || c)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="team-filter-date"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DATE_RANGES.map((d) => <SelectItem key={d.v} value={d.v}>{tf(d.label)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Feed */}
      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="team-activity-empty">
          {tf("Aucune activité pour ces filtres.")}
        </p>
      ) : (
        <ol className="divide-y divide-border/60" data-testid="team-activity-list">
          {data.items.map((it) => (
            <li key={it.id}>
              <button
                onClick={() => it.route && navigate(it.route)}
                className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-secondary/40 rounded-lg px-2 transition-colors"
                data-testid={`team-activity-item-${it.id}`}
              >
                <ActorAvatar actor={it.actor} tf={tf} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">{actorLabel(it.actor, tf)}</div>
                  <div className="text-sm truncate">{it.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {it.subtitle ? `${it.subtitle} · ` : ""}{relativeFr(it.at)}
                  </div>
                </div>
                {it.route && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />}
              </button>
            </li>
          ))}
        </ol>
      )}
      {data.next_cursor && (
        <Button variant="ghost" size="sm" className="w-full" onClick={loadMore} disabled={loadingMore}
          data-testid="team-activity-more">
          {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : tf("Voir plus")}
        </Button>
      )}
    </section>
  );
};
